import { Component, Prop, h, Element, State } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Features type 12 component for SignNow brand feature showcase
 *
 * @product signnow
 * @source relatedlinks
 * @version 1.0.1
 * @category features
 * @contentConfig Features component for showcasing specific use cases and solutions
 * @constant FEATURES_TYPE_12_HEADING - "Find out other Discover the powerful office signature feature with airSlate SignNow" - Main heading
 */
@Component({
  tag: 'sn-features-type-12',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFeaturesType12 {
  @Element() el: HTMLElement;
  @State() cardsCount: number = 0;

  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h3"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h3';

  /**
   * Content alignment: 'left' (default) or 'center'.
   */
  @Prop() contentAlign: 'left' | 'center' = 'center';

  /**
   * Background color theme.
   */
  @Prop() theme: 'white' | 'dark' | 'signnow' = 'white';

  /**
   * @dynamic
   * @type {array}
   * @.type {object}
   * @.property {string} title - Feature card title/heading
   * @.property {string} description - Feature card description text
   * @.property {string} link - Feature card link URL
   * @.required title
   * @minItems 1
   */
  @Prop() items!: any[] | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  componentDidLoad() {
    this.addMicrodataToCards();
  }

  private addMicrodataToCards() {
    const cards = this.el.querySelectorAll('.features-card-type-5');
    cards.forEach((card, index) => {
      const cardElement = card as HTMLElement;
      if (!cardElement.hasAttribute('itemscope')) {
        cardElement.setAttribute('itemscope', '');
        cardElement.setAttribute('itemtype', 'https://schema.org/ListItem');
        cardElement.setAttribute('itemprop', 'itemListElement');
        cardElement.setAttribute('position', String(index + 1));
      }
    });
  }

  render() {
    // CRITICAL: Parse items in render() which is called on BOTH server and client
    // Use local variables, NEVER mutate this.items
    let itemsArray: any[] = [];

    // Try to parse items from string to array (works on both server and client)
    if (typeof this.items === 'string') {
      try {
        const decoded = this.items.replace(/&quot;/g, '"');
        itemsArray = JSON.parse(decoded);
      } catch {
        itemsArray = [];
      }
    } else if (Array.isArray(this.items)) {
      itemsArray = this.items;
    }

    // Update cards count for styling
    this.cardsCount = itemsArray.length;

    // Calculate modifier class based on card count
    let cardsModifier = '';
    if (this.cardsCount === 3) cardsModifier = 'features-type-12__cards--triple';
    if (this.cardsCount === 4) cardsModifier = 'features-type-12__cards--quad';

    const sectionClass = `features-type-12${this.contentAlign === 'center' ? ' features-type-12--centered' : ''} features-type-12--${this.theme}`;

    return (
      <me-section class={sectionClass} yGap="2xl" yGapLg="5xl" role="region" aria-label="Features">
        <me-row>
          <me-column span="12">
            <me-heading align="center" variant="2" tag={this.headingTag}>
              {getConstant(this.constants, 'FEATURES_TYPE_12_HEADING', 'Find out other Discover the powerful office signature feature with airSlate SignNow')}
            </me-heading>
          </me-column>
          <me-column spanLg="12" class={`features-type-12__cards${cardsModifier ? ' ' + cardsModifier : ''}`} itemScope itemType="https://schema.org/ItemList">
            {itemsArray.map((item, index) => (
              <div class="features-card-type-5" key={index}>
                <div class="features-card-type-5__icon">
                  <me-icon name="interface--check-circle"></me-icon>
                </div>
                <me-heading variant="5">
                  <a href={item.link || ''}>{item.title}</a>
                </me-heading>
                {item.description && <me-text>{item.description}</me-text>}
              </div>
            ))}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
